import React, { useState, useRef, useEffect, useCallback } from "react";
import "../styles/gateway/select.css";

/**
 * Custom styled select dropdown.
 *
 * @param {string} value - Currently selected value
 * @param {function} onChange - Called with the new value string
 * @param {Array<{value:string, label:string}>} options - Available options
 * @param {string} [placeholder] - Placeholder text
 * @param {string} [className] - Additional class on wrapper
 * @param {boolean} [disabled] - When true, the trigger can't be opened
 * @param {object} [style] - Inline style on the wrapper
 */
export default function Select({ value, onChange, options, placeholder = "Select...", className = "", disabled = false, style }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const rafRef = useRef(0);

  const selected = options.find((o) => String(o.value) === String(value));

  // Position the menu directly via DOM style — no state/re-render needed
  const positionMenu = useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const r = trigger.getBoundingClientRect();
    const menuH = menu.offsetHeight;
    const fitsBelow = window.innerHeight - r.bottom - 4 >= menuH;
    const top = fitsBelow ? r.bottom + 4 : r.top - menuH - 4;

    menu.style.position = "fixed";
    menu.style.top = `${Math.round(top)}px`;
    menu.style.left = `${Math.round(r.left)}px`;
    menu.style.width = `${Math.round(r.width)}px`;
  }, []);

  // Ref callback — fires synchronously when the menu DOM node mounts
  const menuCallbackRef = useCallback(
    (node) => {
      menuRef.current = node;
      if (!node) return;
      positionMenu();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        positionMenu();
      });
    },
    [positionMenu]
  );

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open, positionMenu]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className={`gwSelect-wrap ${className}`} style={style}>
      <button
        ref={triggerRef}
        type="button"
        className="gwSelect-trigger"
        disabled={disabled}
        onClick={() => setOpen(!open)}
      >
        <span className="gwSelect-value">{selected ? selected.label : placeholder}</span>
        <span className="gwSelect-caret" />
      </button>
      {open && (
        <div ref={menuCallbackRef} className="gwSelect-menu">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`gwSelect-option ${String(value) === String(opt.value) ? "isActive" : ""}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
